import { MutableRefObject, useEffect, useRef } from 'react'

// Waveform bar geometry (CSS pixels): slim rounded bars, the calm voice-memo cadence.
export const WAVE_BAR_WIDTH = 2.5
export const WAVE_BAR_GAP = 2
export const WAVE_BAR_STEP = WAVE_BAR_WIDTH + WAVE_BAR_GAP
export const WAVE_BAR_INTERVAL_MS = 60
export const WAVE_GREEN = '#068466'
export const WAVE_MUTED = '#aba9a9'
export const WAVE_IDLE = '#ecebea'

/** Level (0..1, speech-scaled) under which a moment counts as silence. Room noise sits well below it. */
const SILENCE_LEVEL = 0.09

export const prefersReducedMotion = (): boolean => {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false
  } catch {
    return false
  }
}

const prepareCanvas = (canvas: HTMLCanvasElement | null) => {
  if (!canvas) return null
  const width = canvas.clientWidth
  const height = canvas.clientHeight
  if (!width || !height) return null
  const dpr = window.devicePixelRatio || 1
  if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
    canvas.width = Math.round(width * dpr)
    canvas.height = Math.round(height * dpr)
  }
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, width, height)
  ctx.lineWidth = WAVE_BAR_WIDTH
  ctx.lineCap = 'round'
  return { ctx, width, height }
}

/** Scrolling history of bars, newest at the right edge. */
export const drawLiveWave = (canvas: HTMLCanvasElement | null, history: number[]) => {
  const prepared = prepareCanvas(canvas)
  if (!prepared) return
  const { ctx, width, height } = prepared
  const mid = height / 2
  const maxBars = Math.max(1, Math.floor(width / WAVE_BAR_STEP))
  const start = Math.max(0, history.length - maxBars)
  ctx.strokeStyle = WAVE_GREEN
  ctx.beginPath()
  for (let i = start; i < history.length; i++) {
    const x = width - WAVE_BAR_WIDTH / 2 - (history.length - 1 - i) * WAVE_BAR_STEP
    if (x < WAVE_BAR_WIDTH / 2) continue
    const barHeight = Math.max(WAVE_BAR_WIDTH, history[i] * (height - 4))
    ctx.moveTo(x, mid - barHeight / 2)
    ctx.lineTo(x, mid + barHeight / 2)
  }
  ctx.stroke()
}

/** Reduced motion: a plain level meter instead of the scrolling waveform. */
export const drawLevelMeter = (canvas: HTMLCanvasElement | null, level: number) => {
  const prepared = prepareCanvas(canvas)
  if (!prepared) return
  const { ctx, width, height } = prepared
  const mid = height / 2
  const maxBars = Math.max(1, Math.floor(Math.min(width, 140) / (WAVE_BAR_STEP + 2)))
  const litBars = Math.round(level * maxBars)
  ctx.strokeStyle = WAVE_GREEN
  ctx.beginPath()
  for (let i = 0; i < maxBars; i++) {
    const x = WAVE_BAR_WIDTH / 2 + i * (WAVE_BAR_STEP + 2)
    const barHeight = i < litBars ? height - 4 : WAVE_BAR_WIDTH
    ctx.moveTo(x, mid - barHeight / 2)
    ctx.lineTo(x, mid + barHeight / 2)
  }
  ctx.stroke()
}

/**
 * The take, frozen: the whole recording's levels squeezed to the canvas width.
 * Bars left of `progress` (0..1) are green, the rest muted, so it doubles as the
 * playback scrubber.
 */
export const drawTakeWave = (canvas: HTMLCanvasElement | null, bars: number[], progress: number) => {
  const prepared = prepareCanvas(canvas)
  if (!prepared) return
  const { ctx, width, height } = prepared
  const mid = height / 2
  const n = Math.max(1, Math.floor(width / WAVE_BAR_STEP))
  for (let i = 0; i < n; i++) {
    let peak = 0
    if (bars.length) {
      const a = Math.floor((i * bars.length) / n)
      const b = Math.max(a + 1, Math.floor(((i + 1) * bars.length) / n))
      for (let j = a; j < b && j < bars.length; j++) peak = Math.max(peak, bars[j])
    }
    const x = WAVE_BAR_WIDTH / 2 + i * WAVE_BAR_STEP
    const barHeight = Math.max(WAVE_BAR_WIDTH, peak * (height - 4))
    ctx.strokeStyle = i / n < progress ? WAVE_GREEN : WAVE_MUTED
    ctx.beginPath()
    ctx.moveTo(x, mid - barHeight / 2)
    ctx.lineTo(x, mid + barHeight / 2)
    ctx.stroke()
  }
}

/** Resting state: a dotted baseline. */
export const drawIdleWave = (canvas: HTMLCanvasElement | null) => {
  const prepared = prepareCanvas(canvas)
  if (!prepared) return
  const { ctx, width, height } = prepared
  ctx.strokeStyle = WAVE_IDLE
  ctx.beginPath()
  for (let x = WAVE_BAR_WIDTH / 2; x < width; x += WAVE_BAR_STEP) {
    ctx.moveTo(x, height / 2 - WAVE_BAR_WIDTH / 2)
    ctx.lineTo(x, height / 2 + WAVE_BAR_WIDTH / 2)
  }
  ctx.stroke()
}

interface MicLevelOptions {
  stream: MediaStream | null | undefined
  active: boolean
  canvasRef: MutableRefObject<HTMLCanvasElement | null>
  /** Called once when nothing above room noise has been heard for this long. */
  silenceMs?: number
  onSilence?: () => void
}

/**
 * Taps the SAME getUserMedia stream the MediaRecorder records (no second capture
 * stream) with an AnalyserNode. Analysis only: the source connects to the
 * analyser and nowhere else, never to the destination (no feedback) and never in
 * the recorder's path. Draws the live waveform (a level meter under reduced
 * motion), keeps the full level history for the frozen take, and reports long
 * silences. Everything is torn down when `active` goes false.
 */
const useMicLevels = ({ stream, active, canvasRef, silenceMs, onSilence }: MicLevelOptions) => {
  const historyRef = useRef<number[]>([])
  const onSilenceRef = useRef(onSilence)
  onSilenceRef.current = onSilence

  useEffect(() => {
    if (!active || !stream) return
    const AudioContextCtor: any = window.AudioContext || (window as any).webkitAudioContext
    if (!AudioContextCtor) return

    let audioContext: AudioContext
    let source: MediaStreamAudioSourceNode
    let analyser: AnalyserNode
    try {
      audioContext = new AudioContextCtor()
      source = audioContext.createMediaStreamSource(stream)
      analyser = audioContext.createAnalyser()
      analyser.fftSize = 512
      analyser.smoothingTimeConstant = 0.7
      source.connect(analyser)
    } catch (e) {
      return // no visualisation; the recording itself is unaffected
    }

    const reduced = prefersReducedMotion()
    const timeData = new Uint8Array(analyser.fftSize)
    historyRef.current = []
    let running = true
    let rafId = 0
    let meterInterval: ReturnType<typeof setInterval> | null = null
    let lastBarAt = 0
    let peakSinceBar = 0
    let lastSoundAt = performance.now()
    let silenceFired = false

    const readLevel = () => {
      analyser.getByteTimeDomainData(timeData)
      let sum = 0
      for (let i = 0; i < timeData.length; i++) {
        const v = (timeData[i] - 128) / 128
        sum += v * v
      }
      const rms = Math.sqrt(sum / timeData.length)
      return Math.min(1, Math.pow(rms * 3.5, 0.85))
    }

    const checkSilence = (level: number, now: number) => {
      if (level >= SILENCE_LEVEL) lastSoundAt = now
      if (!silenceFired && silenceMs && now - lastSoundAt >= silenceMs) {
        silenceFired = true
        onSilenceRef.current?.()
      }
    }

    // One bar per interval keeps the frozen take the same shape whichever mode drew it.
    const sample = (now: number) => {
      const level = readLevel()
      peakSinceBar = Math.max(peakSinceBar, level)
      if (!lastBarAt) lastBarAt = now
      if (now - lastBarAt >= WAVE_BAR_INTERVAL_MS) {
        historyRef.current.push(peakSinceBar)
        peakSinceBar = 0
        lastBarAt = now
      }
      checkSilence(level, now)
      return level
    }

    if (reduced) {
      // Sampling still runs often enough for the take and silence detection; only the drawing is calm.
      let lastDraw = 0
      meterInterval = setInterval(() => {
        if (!running) return
        const now = performance.now()
        const level = sample(now)
        if (now - lastDraw >= 250) {
          drawLevelMeter(canvasRef.current, level)
          lastDraw = now
        }
      }, WAVE_BAR_INTERVAL_MS)
    } else {
      const loop = (now: number) => {
        if (!running) return
        sample(now)
        drawLiveWave(canvasRef.current, historyRef.current)
        rafId = requestAnimationFrame(loop)
      }
      rafId = requestAnimationFrame(loop)
    }

    return () => {
      running = false
      if (rafId) cancelAnimationFrame(rafId)
      if (meterInterval) clearInterval(meterInterval)
      try {
        source.disconnect()
      } catch (e) {}
      try {
        analyser.disconnect()
      } catch (e) {}
      if (audioContext.state !== 'closed') {
        audioContext.close().catch(() => undefined)
      }
    }
  }, [active, stream, silenceMs])

  return historyRef
}

export default useMicLevels
